import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Monitor, Briefcase, Mail, ExternalLink } from 'lucide-react'
import { menuApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import { useScrollPosition } from '../../hooks/useScrollPosition'
import { EXTERNAL_LINKS } from '../../constants/contact'
import { useContactHref } from '../../hooks/useContactHref'
import { buildNavItems, DEFAULT_WE_ARE_MAYAR, DEFAULT_OUR_COMMITMENT, DEFAULT_OUR_SCHOOLS, DEFAULT_OUR_STUDENTS, DEFAULT_FOR_PARENTS, DEFAULT_ADMISSIONS, DEFAULT_SCHOOL_DIVISIONS } from '../../utils/menu'
import { ensureEnglishForCareers } from '../../utils/careers'
import Button from '../ui/Button'
import NavDropdown from './NavDropdown'
import MobileNavItem from './MobileNavItem'
import TopBar from './TopBar'

const DEFAULT_MENU = [
  { id: 'home', title_en: 'Home', title_ar: 'الرئيسية', url: '/', children: [] },
  DEFAULT_WE_ARE_MAYAR,
  DEFAULT_SCHOOL_DIVISIONS,
  DEFAULT_OUR_SCHOOLS,
  DEFAULT_ADMISSIONS,
  DEFAULT_FOR_PARENTS,
  DEFAULT_OUR_STUDENTS,
  DEFAULT_OUR_COMMITMENT,
]

export default function Header({ transparent = false }) {
  const { t } = useTranslation()
  const { localized, isRTL, language, setLanguage } = useLanguage()
  const scrolled = useScrollPosition(80)
  const location = useLocation()
  const contactHref = useContactHref()
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuItems, setMenuItems] = useState(DEFAULT_MENU)

  const isHome = location.pathname === '/'
  const isTransparent = transparent && isHome && !scrolled

  useEffect(() => {
    menuApi
      .getAll()
      .then(({ data }) => {
        const tree = data.data || data
        const items = buildNavItems(tree)
        if (items?.length) setMenuItems(items)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  const resolveUrl = (item) => {
    if (item.url) return item.url
    if (item.slug) return `/pages/${item.slug}`
    return null
  }

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <TopBar />

      <div
        className={`transition-all duration-500 ${
          isTransparent
            ? 'bg-transparent py-3 sm:py-4 lg:py-5'
            : 'bg-white/95 py-2.5 shadow-md shadow-primary/5 backdrop-blur-lg sm:py-3'
        }`}
      >
        <div className="mx-auto flex max-w-7xl min-w-0 items-center justify-between gap-2 px-4 sm:gap-3 sm:px-6 lg:px-8">
          <Link
            to="/"
            onClick={() => {
              window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
              document.documentElement.scrollTop = 0
              document.body.scrollTop = 0
            }}
            className="relative z-10 min-w-0 flex-shrink-0 bg-transparent"
          >
            <img
              src={isTransparent ? '/logo/logo-white.png' : '/logo/logo-color.png'}
              alt={t('brand.name')}
              width={140}
              height={56}
              className="h-10 w-auto max-w-[140px] bg-transparent object-contain transition-all duration-500 sm:h-12 sm:max-w-none lg:h-14"
              onError={(e) => {
                e.target.style.display = 'none'
                e.target.nextSibling?.classList.remove('hidden')
              }}
            />
            <span
              className={`hidden text-lg font-bold transition-colors duration-500 sm:text-xl ${
                isTransparent ? 'text-white' : 'text-primary'
              }`}
            >
              {t('brand.shortName')}
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {menuItems.map((item) => {
              if (item.children?.length) {
                return (
                  <NavDropdown
                    key={item.id || item.slug}
                    item={item}
                    isTransparent={isTransparent}
                    location={location}
                  />
                )
              }

              const url = resolveUrl(item)
              if (!url) return null

              const label = localized(item, 'title')
              const isActive =
                url === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(url.split('#')[0]) && url !== '/'

              return (
                <Link
                  key={item.id || url}
                  to={url}
                  className={`relative rounded-lg px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                    isTransparent
                      ? isActive
                        ? 'text-accent'
                        : 'text-white/90 hover:text-white'
                      : isActive
                        ? 'text-primary'
                        : 'text-text/70 hover:text-primary'
                  }`}
                >
                  {label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-indicator"
                      className="absolute inset-x-2 -bottom-0.5 h-0.5 rounded-full bg-accent"
                    />
                  )}
                </Link>
              )
            })}
          </nav>

          <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-3">
            <Button
              to="/apply"
              variant={isTransparent ? 'outline' : 'accent'}
              size="sm"
              className="hidden min-[480px]:inline-flex"
            >
              {t('nav.applyNow')}
            </Button>

            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className={`rounded-lg p-2.5 transition-colors lg:hidden ${
                menuOpen
                  ? 'bg-primary/10 text-primary'
                  : isTransparent
                    ? 'text-white hover:bg-white/10'
                    : 'text-primary hover:bg-primary/5'
              }`}
              aria-label={menuOpen ? t('nav.closeMenu') : t('nav.menu')}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.button
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 top-[var(--header-offset)] z-40 bg-black/40 lg:hidden"
              onClick={() => setMenuOpen(false)}
              aria-label={t('nav.closeMenu')}
            />
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className={`fixed inset-x-0 top-[var(--header-offset)] z-50 max-h-[calc(100dvh-var(--header-offset))] overflow-y-auto border-t border-border/30 bg-white shadow-xl lg:hidden ${
                isRTL ? 'text-right' : 'text-left'
              }`}
            >
              <nav className="flex flex-col gap-2 px-4 py-4 pb-8">
                <div className="mb-2 grid grid-cols-1 gap-2 border-b border-border/40 pb-4 min-[480px]:grid-cols-3">
                  <a
                    href={EXTERNAL_LINKS.eSchool}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary/5 px-3 py-3 text-sm font-semibold text-primary"
                  >
                    <Monitor className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="truncate">{t('nav.eSchool')}</span>
                    <ExternalLink className="hidden h-3 w-3 shrink-0 opacity-50 min-[480px]:inline" aria-hidden />
                  </a>
                  <Link
                    to="/careers"
                    onClick={() => {
                      ensureEnglishForCareers(language, setLanguage)
                      setMenuOpen(false)
                    }}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-accent/10 px-3 py-3 text-sm font-semibold text-primary"
                  >
                    <Briefcase className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="truncate">{t('nav.jobApplication')}</span>
                  </Link>
                  <Link
                    to={contactHref}
                    onClick={() => setMenuOpen(false)}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-secondary/10 px-3 py-3 text-sm font-semibold text-primary"
                  >
                    <Mail className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="truncate">{t('nav.contact')}</span>
                  </Link>
                </div>
                {menuItems.map((item) => (
                  <MobileNavItem
                    key={item.id || resolveUrl(item)}
                    item={item}
                    resolveUrl={resolveUrl}
                    onNavigate={() => setMenuOpen(false)}
                  />
                ))}
                <Button to="/apply" variant="accent" size="md" className="mt-2 w-full sm:hidden">
                  {t('nav.applyNow')}
                </Button>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  )
}
